/* ls-stade-motion-film — "VIVRE LA TUILIÈRE" film roll on the native aiconeo
 * section (p70). Reference choreography: assets/ls-stade-film.js.
 *
 * Native structure (recognised by element TYPE and order, never ids/text):
 *   the section holds exactly ONE content-slider in `layout: 'rail'` (the
 *   native horizontal scroller = the static state); every slide holds a photo
 *   (image element), usually followed by its caption heading. Any photo count.
 *   The two frame heights are each photo's own aspect-ratio field.
 *
 * Motion (theme only, nothing here writes content):
 *   - all widths: `.ls-film--motion` staggers the frame tops a few pixels
 *     (margins, never transforms: the slides are scroll-snap targets) and
 *     top-aligns the strip — the editorial rhythm of the original.
 *   - ≥750px: `.ls-film--scrub` swaps the rail's scroller for a clipped,
 *     snap-free viewport; when the section top meets the sticky header the
 *     whole section PINS and the vertical scroll drives the track right→left,
 *     ~1px of pan per 1px of scroll, releasing when the last photo (trailing
 *     bleed padding included) is fully in view. A fit pass shrinks the shared
 *     slide width (`--ls-film-w`, runtime only) so the pinned section fits
 *     below the header, like the original's height-token fit.
 *   - <750px: no pin — the native swipe scroller stays (as the original).
 * Reduced motion, no GSAP or an unrecognised structure → untouched native page.
 */
(function () {
  'use strict';
  if (!window.LsStadeMotion) return;

  var MIN_FRAME_H = 160;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function childrenWith(parent, cls) {
    return parent ? toArray(parent.children).filter(function (n) { return n.classList.contains(cls); }) : [];
  }

  function headerOffset() {
    var header = document.querySelector('.shopify-section-header, .section-header');
    return header ? Math.round(header.getBoundingClientRect().height) : 0;
  }

  LsStadeMotion.scene('film', function (section, ctx) {
    if (ctx.reduced || !ctx.mm) return null;

    // Exactly one top-level rail slider.
    var sliders = ctx.items('slider').filter(function (s) {
      return !s.parentElement.closest('.neo-slider');
    });
    if (sliders.length !== 1) return null;
    var rail = sliders[0];
    if (rail.getAttribute('data-neo-slider-layout') !== 'rail') return null;
    var frame = childrenWith(rail, 'neo-slider__rail-frame')[0];
    var viewport = childrenWith(frame, 'neo-slider__viewport')[0];
    var track = childrenWith(viewport, 'neo-slider__track')[0];
    var slides = childrenWith(track, 'neo-slider__slide');
    if (!slides.length) return null;
    var photos = [];
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('.content-type-neo-image img');
      if (!img) return null;
      photos.push(img);
    }

    section.classList.add('ls-film--motion');

    ctx.mm.add('(min-width: 750px)', function () {
      var lazy = [];
      var tween = null;

      function distance() {
        return Math.max(track.offsetWidth - viewport.clientWidth, 0);
      }

      /* Natural height: the pin stamps an inline height on the section, so
         measure its content box + paddings instead of the section itself. */
      function naturalHeight() {
        var cs = getComputedStyle(section);
        var h = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
        toArray(section.children).forEach(function (c) { h += c.offsetHeight; });
        return h;
      }

      /* The strip pans off the SCREEN's left edge (as the original), not the
         page gutter: pull the viewport out to x=0 and pad the track by the
         same amount so the static start position is unchanged. Layout
         offsets, not rects, so a pinned/transformed ancestor cannot skew it. */
      function lead() {
        section.style.removeProperty('--ls-film-lead');
        var x = 0;
        for (var n = viewport; n; n = n.offsetParent) x += (n.offsetLeft || 0) + (n !== viewport ? n.clientLeft || 0 : 0);
        section.style.setProperty('--ls-film-lead', Math.max(0, Math.round(x)) + 'px');
      }

      /* Shrink the shared slide width until the section fits below the
         header. Captions may re-wrap at the new width, so re-measure. */
      function fit() {
        lead();
        section.classList.remove('ls-film--fit');
        section.style.removeProperty('--ls-film-w');
        var visible = Math.max(window.innerHeight - headerOffset(), 1);
        for (var pass = 0; pass < 3; pass++) {
          var over = naturalHeight() - visible;
          if (over <= 0) return;
          var tallest = 0;
          photos.forEach(function (p) { tallest = Math.max(tallest, p.offsetHeight); });
          var width = slides[0].offsetWidth;
          if (!tallest || !width || tallest <= MIN_FRAME_H) return;
          var factor = Math.max(tallest - over, MIN_FRAME_H) / tallest;
          if (factor >= 1) return;
          section.style.setProperty('--ls-film-w', Math.floor(width * factor) + 'px');
          section.classList.add('ls-film--fit');
        }
      }

      /* A focused link inside a slide would scroll the clipped viewport
         natively; turn that into page scroll to the matching pan instead. */
      function onScroll() {
        if (viewport.scrollLeft !== 0) viewport.scrollLeft = 0;
      }
      function onFocusIn(e) {
        var st = tween && tween.scrollTrigger;
        var slide = e.target.closest && e.target.closest('.neo-slider__slide');
        if (!st || !slide) return;
        var d = distance();
        var lead = parseFloat(section.style.getPropertyValue('--ls-film-lead')) || 0;
        var p = d ? Math.min(Math.max((slide.offsetLeft - lead) / d, 0), 1) : 0;
        viewport.scrollLeft = 0;
        window.scrollTo(0, st.start + p * (st.end - st.start));
      }

      section.classList.add('ls-film--scrub');
      viewport.scrollLeft = 0;
      // Photos travel in from the right while pinned: fetch them up front.
      photos.forEach(function (p) {
        if (p.loading === 'lazy') { lazy.push(p); p.loading = 'eager'; }
      });
      fit();

      if (distance() >= 1) {
        tween = gsap.fromTo(track, { x: 0 }, {
          x: function () { return -distance(); },
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: function () { return 'top ' + headerOffset() + 'px'; },
            end: function () { return '+=' + distance(); },
            pin: true,
            anticipatePin: 1,
            scrub: 0.6,
            invalidateOnRefresh: true
          }
        });
        ScrollTrigger.addEventListener('refreshInit', fit);
        viewport.addEventListener('scroll', onScroll, { passive: true });
        viewport.addEventListener('focusin', onFocusIn);
        // Re-created after a breakpoint crossing: keep document order.
        ScrollTrigger.sort();
      } else {
        // The strip fits: nothing to pan, the native scroller stays.
        section.classList.remove('ls-film--scrub', 'ls-film--fit');
        section.style.removeProperty('--ls-film-w');
        section.style.removeProperty('--ls-film-lead');
      }

      return function () {
        ScrollTrigger.removeEventListener('refreshInit', fit);
        viewport.removeEventListener('scroll', onScroll);
        viewport.removeEventListener('focusin', onFocusIn);
        section.classList.remove('ls-film--scrub', 'ls-film--fit');
        section.style.removeProperty('--ls-film-w');
        section.style.removeProperty('--ls-film-lead');
        ['transform', 'translate', 'rotate', 'scale', 'will-change'].forEach(function (p) {
          track.style.removeProperty(p);
        });
        lazy.forEach(function (p) { p.loading = 'lazy'; });
        viewport.scrollLeft = 0;
      };
    });

    return null;
  });
})();
